// Persona taxonomy used by the onboarding wizard to tailor the app to each
// user's job-to-be-done. Stored on Clerk's unsafeMetadata as { persona,
// useCases, goals, onboardingComplete, onboardingVersion }.

export type PersonaId =
  | "recruiter"
  | "sales"
  | "real-estate"
  | "consultant"
  | "creator"
  | "healthcare"
  | "executive"
  | "educator"
  | "hospitality"
  | "other";

export interface Persona {
  id: PersonaId;
  label: string;
  tagline: string;
}

export const PERSONAS: Persona[] = [
  { id: "recruiter",   label: "Recruiter",      tagline: "Source and connect with talent" },
  { id: "sales",       label: "Sales",          tagline: "Close more deals, faster" },
  { id: "real-estate", label: "Real Estate",    tagline: "Listings, tours, and showings" },
  { id: "consultant",  label: "Consultant",     tagline: "Advise clients with credibility" },
  { id: "creator",     label: "Creator",        tagline: "Build audience and brand" },
  { id: "healthcare",  label: "Healthcare",     tagline: "Patient-friendly contact" },
  { id: "executive",   label: "Executive",      tagline: "Lead a company or team" },
  { id: "educator",    label: "Educator",       tagline: "Teach, train, and mentor" },
  { id: "hospitality", label: "Cafe or restaurant", tagline: "Guest WiFi, menus, reviews" },
  { id: "other",       label: "Something else", tagline: "Mix and match what fits" },
];

export interface UseCase {
  id: string;
  label: string;
  description: string;
}

// Use cases shown in step 2 — chosen as multi-select. Drives which sections
// of the dashboard, link presets, and tips we surface for the user.
export const USE_CASES: Record<PersonaId, UseCase[]> = {
  recruiter: [
    { id: "candidate-intake", label: "Candidate intake", description: "Capture leads from career fairs and events" },
    { id: "interview-scheduling", label: "Interview scheduling", description: "Calendly / Google Calendar booking link" },
    { id: "open-roles", label: "Share open roles", description: "Link to your job board or ATS" },
    { id: "careers-page", label: "Careers page", description: "Drive traffic to your company hiring page" },
    { id: "linkedin-connect", label: "Grow my LinkedIn", description: "Be easy to connect with on LinkedIn" },
    { id: "team-bio", label: "My team bio", description: "Show off your work and people" },
  ],
  sales: [
    { id: "book-demo", label: "Book a demo", description: "Calendar booking front-and-center" },
    { id: "pitch-deck", label: "Pitch deck", description: "Share your latest deck or one-pager" },
    { id: "case-studies", label: "Case studies", description: "Social proof on tap" },
    { id: "pricing", label: "Pricing", description: "Send people straight to your pricing page" },
    { id: "payment-link", label: "Take a payment", description: "Stripe / Square / PayPal link" },
    { id: "lead-capture", label: "Capture leads", description: "Collect contacts from every tap" },
  ],
  "real-estate": [
    { id: "listings", label: "Active listings", description: "Link directly to your IDX/MLS feed" },
    { id: "virtual-tours", label: "Virtual tours", description: "Share Matterport / video walk-throughs" },
    { id: "showings", label: "Schedule showings", description: "Calendar booking for buyers" },
    { id: "mortgage", label: "Mortgage calculator", description: "Help buyers run the numbers" },
    { id: "neighborhood", label: "Neighborhood guide", description: "Share what makes the area shine" },
    { id: "reviews", label: "Client reviews", description: "Zillow, Google, or testimonials" },
  ],
  consultant: [
    { id: "book-consult", label: "Book a consultation", description: "First call on the calendar" },
    { id: "portfolio", label: "Portfolio / case studies", description: "Show what you've shipped" },
    { id: "payment-link", label: "Take a payment", description: "Invoice or retainer link" },
    { id: "white-papers", label: "White papers", description: "Long-form work for credibility" },
    { id: "linkedin", label: "LinkedIn", description: "Be easy to vet" },
    { id: "speaking", label: "Speaking & podcasts", description: "Share recent appearances" },
  ],
  creator: [
    { id: "youtube", label: "YouTube channel", description: "One tap to your latest video" },
    { id: "tiktok", label: "TikTok", description: "Send people to your latest" },
    { id: "instagram", label: "Instagram", description: "Quick follow link" },
    { id: "newsletter", label: "Newsletter signup", description: "Beehiiv, Substack, ConvertKit" },
    { id: "merch", label: "Merch store", description: "Drive sales straight from a tap" },
    { id: "podcast", label: "Podcast feed", description: "Apple, Spotify, RSS" },
  ],
  healthcare: [
    { id: "scheduling", label: "Patient scheduling", description: "Book an appointment online" },
    { id: "intake-form", label: "Intake form", description: "Pre-visit paperwork" },
    { id: "telehealth", label: "Telehealth", description: "Virtual visit link" },
    { id: "insurance", label: "Accepted insurance", description: "What you take, at a glance" },
    { id: "portal", label: "Patient portal", description: "Records, prescriptions, billing" },
    { id: "after-hours", label: "After-hours line", description: "Emergency contact info" },
  ],
  executive: [
    { id: "linkedin", label: "LinkedIn", description: "Headline, role, and reach" },
    { id: "calendar", label: "Calendar booking", description: "Make time easy to find" },
    { id: "company-page", label: "Company page", description: "What you build and where" },
    { id: "press-kit", label: "Press kit", description: "Bio, headshot, talking points" },
    { id: "investor-deck", label: "Investor deck", description: "Fundraise-ready materials" },
    { id: "board-bio", label: "Board / advisor bio", description: "Other roles you serve" },
  ],
  educator: [
    { id: "course-catalog", label: "Course catalog", description: "Browse what you teach" },
    { id: "office-hours", label: "Office hours", description: "Booking for students" },
    { id: "syllabus", label: "Syllabus / resources", description: "Direct download or link" },
    { id: "research", label: "Research papers", description: "ORCID, Scholar, or PDF" },
    { id: "linkedin", label: "LinkedIn", description: "Professional bio" },
    { id: "speaking", label: "Speaking & workshops", description: "Where you've presented" },
  ],
  hospitality: [
    { id: "guest-wifi", label: "Guest WiFi", description: "Tap to connect, no asking the staff" },
    { id: "menu", label: "Digital menu", description: "QR menu with photos and pricing" },
    { id: "reviews", label: "Collect reviews", description: "Steer happy guests to Google or Yelp" },
    { id: "punch-card", label: "Punch / loyalty card", description: "Reward repeat visits" },
    { id: "reservations", label: "Reservations", description: "OpenTable, Resy, or your own form" },
    { id: "events", label: "Events and specials", description: "Promote what's happening this week" },
  ],
  other: [
    { id: "linkedin", label: "LinkedIn", description: "The classic professional link" },
    { id: "resume", label: "Resume / CV", description: "PDF download" },
    { id: "calendar", label: "Calendar", description: "Booking link" },
    { id: "portfolio", label: "Portfolio", description: "Anything you've made" },
    { id: "contact", label: "Contact me", description: "Email or phone, your choice" },
    { id: "custom", label: "Something custom", description: "We'll let you build your own" },
  ],
};

export interface Goal {
  id: string;
  label: string;
}

export const GOALS: Goal[] = [
  { id: "more-leads",        label: "Generate more leads" },
  { id: "first-impression",  label: "Make a strong first impression" },
  { id: "physical-card",     label: "Get a physical NFC card" },
  { id: "team",              label: "Build a team workspace" },
  { id: "analytics",         label: "Track who taps my card" },
  { id: "ditch-paper",       label: "Stop printing paper cards" },
];

export interface OnboardingAnswers {
  persona: PersonaId | null;
  useCases: string[];
  goals: string[];
  onboardingComplete: boolean;
  onboardingVersion: number;
  completedAt?: string;
}

export const ONBOARDING_VERSION = 1;
